/**
 * 角色操作相关Hooks
 * 包含角色的CRUD、列表管理、统计、选择等功能
 */
import React, { useState, useEffect } from "react";
import { message } from "antd";
import { Role } from "../config/panelTableConfig";
import { roleApi } from "../utils/roleApi";
import { transformFormDataToApiFormat } from "../utils/roleUtils";

// ==================== 类型定义 ====================

export interface RoleStats {
  total: number;
  active: number;
  inactive: number;
}

// ==================== 角色统计Hook ====================

/**
 * 角色统计信息hook
 * @param roleList 角色列表
 * @returns 统计信息和加载函数
 */
export const useRoleStats = (roleList: Role[]) => {
  const [roleStats, setRoleStats] = useState<RoleStats>({
    total: 0,
    active: 0,
    inactive: 0,
  });

  // 加载统计信息
  const loadRoleStats = () => {
    const stats = {
      total: roleList.length,
      active: roleList.filter((role) => role.status === "active").length,
      inactive: roleList.filter((role) => role.status === "inactive").length,
    };
    setRoleStats(stats);
  };

  // 当角色列表更新时重新计算统计信息
  useEffect(() => {
    loadRoleStats();
  }, [roleList]);

  return { roleStats, loadRoleStats };
};

// ==================== 角色列表管理Hook ====================

/**
 * 角色列表管理hook
 * @returns 角色列表相关状态和方法
 */
export const useRoleList = () => {
  const [roleList, setRoleList] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useState({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [total, setTotal] = useState(0);

  // 获取角色列表
  const fetchRoleList = async (params: any = {}) => {
    setLoading(true);
    try {
      const response = await roleApi.getRoleList(params);
      if (response.success) {
        setRoleList(response.data?.items || []);
        setTotal(response.data?.pagination?.total || response.data?.total || 0);
      }
    } catch (error) {
      console.error("获取角色列表失败:", error);
    } finally {
      setLoading(false);
    }
  };

  // 搜索处理
  const handleSearch = async (params: any = {}) => {
    // console.debug("[RolesPage] handleSearch 被调用，参数:", params);
    const searchData = { ...searchParams, ...params };
    setSearchParams(searchData);

    const paginationData = {
      current: pagination.current,
      pageSize: pagination.pageSize,
      ...searchData,
    };

    // console.debug("[RolesPage] 执行搜索，分页数据:", paginationData);
    await fetchRoleList(paginationData);
  };

  // 重置搜索
  const handleReset = () => {
    // console.debug("[RolesPage] handleReset 被调用");
    setSearchParams({});
    setPagination({ current: 1, pageSize: 10 });
    fetchRoleList({ current: 1, pageSize: 10 });
  };

  // 分页处理
  const handlePaginationChange = (paginationInfo: any) => {
    const newPagination = {
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    };
    setPagination(newPagination);

    const paginationData = {
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
      ...searchParams,
    };

    fetchRoleList(paginationData);
  };

  return {
    roleList,
    setRoleList,
    loading,
    setLoading,
    searchParams,
    setSearchParams,
    pagination,
    setPagination,
    total,
    setTotal,
    fetchRoleList,
    handleSearch,
    handleReset,
    handlePaginationChange,
  };
};

// ==================== 角色CRUD操作Hook ====================

/**
 * 角色CRUD操作hook
 * @param onSuccess 成功回调
 * @returns CRUD操作方法
 */
export const useRoleCRUD = (onSuccess: () => void) => {
  // 创建角色
  const handleCreate = async (values: any) => {
    try {
      const response = await roleApi.createRole(values);

      if (response.success) {
        message.success("角色创建成功");
        onSuccess();
        return true;
      } else {
        message.error(response.message || "角色创建失败");
        return false;
      }
    } catch (error) {
      console.error("创建角色失败:", error);
      message.error("角色创建失败");
      return false;
    }
  };

  // 编辑角色
  const handleEdit = async (values: any, currentRecord: Role | null) => {
    if (!currentRecord) return false;

    try {
      // 转换表单数据为API格式
      const apiData = transformFormDataToApiFormat(values);
      const response = await roleApi.updateRole(currentRecord.id, apiData);

      if (response.success) {
        message.success("角色更新成功");
        onSuccess();
        return true;
      } else {
        message.error(response.message || "角色更新失败");
        return false;
      }
    } catch (error) {
      console.error("更新角色失败:", error);
      message.error("角色更新失败");
      return false;
    }
  };

  // 删除角色
  const handleDelete = async (record: Role, onSuccess: () => void) => {
    try {
      const response = await roleApi.deleteRole(record.id);

      if (response.success) {
        message.success("角色删除成功");
        onSuccess();
        return true;
      } else {
        // 显示后端返回的具体错误信息
        const errorMessage = response.message || "角色删除失败";
        message.error(errorMessage);
        return false;
      }
    } catch (error) {
      console.error("删除角色失败:", error);
      // 尝试从错误对象中提取错误信息
      let errorMessage = "角色删除失败";
      if (error instanceof Error) {
        // 如果错误消息不是默认的 HTTP 错误格式，使用它
        if (error.message && !error.message.startsWith("HTTP")) {
          errorMessage = error.message;
        } else if (error.message) {
          // 如果是 HTTP 错误，尝试提取更友好的错误信息
          errorMessage = error.message;
        }
      }
      message.error(errorMessage);
      return false;
    }
  };

  // 批量删除角色
  const handleBatchDelete = async (selectedRowKeys: React.Key[], onSuccess: () => void) => {
    try {
      const ids = selectedRowKeys.map((key) => String(key));
      const response = await roleApi.batchDeleteRoles(ids);

      if (response.success) {
        const { deletedIds, errorIds, notFoundIds } = response.data;
        let messageText = `成功删除 ${deletedIds.length} 个角色`;

        if (errorIds.length > 0) {
          messageText += `，${errorIds.length} 个角色删除失败（可能有关联的管理员）`;
        }
        if (notFoundIds.length > 0) {
          messageText += `，${notFoundIds.length} 个角色未找到`;
        }

        message.success(messageText);
        onSuccess();
        return true;
      } else {
        message.error(response.message || "批量删除失败");
        return false;
      }
    } catch (error) {
      console.error("批量删除角色失败:", error);
      message.error("批量删除失败");
      return false;
    }
  };

  // 切换状态
  const handleToggleStatus = async (record: Role, onSuccess: () => void) => {
    const newStatus = record.status === "active" ? "inactive" : "active";
    const action = newStatus === "active" ? "启用" : "禁用";

    try {
      const response = await roleApi.updateRole(record.id, {
        isActive: newStatus === "active",
      });

      if (response.success) {
        message.success(`角色${action}成功`);
        onSuccess();
      } else {
        message.error(response.message || `角色${action}失败`);
      }
    } catch (error) {
      console.error("切换角色状态失败:", error);
      message.error(`角色${action}失败`);
    }
  };

  return {
    handleCreate,
    handleEdit,
    handleDelete,
    handleBatchDelete,
    handleToggleStatus,
  };
};

// ==================== 角色选择管理Hook ====================

/**
 * 角色选择管理hook
 * @returns 选择相关状态和方法
 */
export const useRoleSelection = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<Role[]>([]);

  // 处理行选择变化
  const handleRowSelectionChange = (selectedRowKeys: React.Key[], selectedRows: Role[]) => {
    // console.debug("行选择变化", { selectedRowKeys, selectedRows });
    setSelectedRowKeys(selectedRowKeys);
    setSelectedRows(selectedRows);
  };

  // 清空选择
  const clearSelection = () => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
  };

  return {
    selectedRowKeys,
    setSelectedRowKeys,
    selectedRows,
    setSelectedRows,
    handleRowSelectionChange,
    clearSelection,
  };
};

// ==================== 角色弹窗状态管理Hook ====================

/**
 * 角色弹窗状态管理hook
 * @returns 弹窗状态和操作方法
 */
export const useRoleModals = () => {
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<Role | null>(null);

  // 显示创建弹窗
  const showCreateModal = () => {
    setCurrentRecord(null);
    setCreateModalVisible(true);
  };

  // 显示编辑弹窗
  const showEditModal = (record: Role) => {
    setCurrentRecord(record);
    setEditModalVisible(true);
  };

  // 关闭创建弹窗
  const hideCreateModal = () => {
    setCreateModalVisible(false);
    setCurrentRecord(null);
  };

  // 关闭编辑弹窗
  const hideEditModal = () => {
    setEditModalVisible(false);
    setCurrentRecord(null);
  };

  return {
    createModalVisible,
    editModalVisible,
    currentRecord,
    showCreateModal,
    showEditModal,
    hideCreateModal,
    hideEditModal,
    setCurrentRecord,
  };
};
